import { capture, registerPwaSuperProperties } from './analytics.js';
import { getInstallContext } from './pwaDetection.js';
import { markInstalled, getDebugSnapshot } from './installPromptManager.js';
import { INSTALL_SOURCE_KEY } from '../components/InstallBanner/installBannerLogic.js';

let deferredPrompt = null;
let initialized = false;

function readInstallSource() {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    return sessionStorage.getItem(INSTALL_SOURCE_KEY);
  } catch {
    return null;
  }
}

function clearInstallSource() {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.removeItem(INSTALL_SOURCE_KEY);
  } catch { /* ignore */ }
}

function safeCapture(event, props) {
  try {
    capture(event, props);
  } catch { /* ignore */ }
}

export function initInstallPromptCapture() {
  if (initialized) return;
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;
  initialized = true;

  window.addEventListener('beforeinstallprompt', (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    deferredPrompt = e || null;
    safeCapture('pwa_install_prompt_available', getInstallContext());
  });

  window.addEventListener('appinstalled', () => {
    // Skip if we already emitted an inferred `pwa_installed` on the
    // accepted prompt path (some browsers fire both). The state flag is
    // the dedup signal — markInstalled() was called from the inferred
    // path, so getDebugSnapshot().installed is already true here.
    const alreadyInstalled = getDebugSnapshot().installed;
    const source = readInstallSource() || 'unknown';
    clearInstallSource();
    try { markInstalled(); } catch { /* ignore */ }
    // Register super-property BEFORE capturing the event so `pwa_installed`
    // itself carries pwa_installed=true. Funnels that filter on the super
    // would otherwise exclude the install event from their own funnel.
    try { registerPwaSuperProperties({ pwa_installed: true }); } catch { /* ignore */ }
    if (!alreadyInstalled) {
      safeCapture('pwa_installed', { source });
    }
    deferredPrompt = null;
  });
}

export function hasNativePrompt() {
  return deferredPrompt !== null;
}

export async function triggerNativePrompt() {
  if (!deferredPrompt) {
    // Caller optimistically wrote `installSource` before calling us; clear
    // it so a later install via browser menu isn't misattributed to the
    // banner CTA. Reachable on a race between hasNativePrompt() and here.
    clearInstallSource();
    safeCapture('pwa_native_prompt_unavailable', getInstallContext());
    return { outcome: 'unavailable' };
  }
  const prompt = deferredPrompt;
  try {
    await prompt.prompt();
    const choice = await prompt.userChoice;
    const outcome = (choice && choice.outcome) || 'dismissed';
    // Some browsers fail to fire `appinstalled` even after accept; treat
    // an accepted prompt as authoritative so we don't re-show the banner.
    // Mark installed + register super-property BEFORE capturing the outcome
    // event so the event itself carries pwa_installed=true.
    let inferredInstall = null;
    if (outcome === 'accepted') {
      // Some browsers fail to fire `appinstalled` even after accept, so we
      // emit an inferred `pwa_installed` ourselves. Mark state + register the
      // super-property BEFORE the outcome event so it carries
      // pwa_installed=true; but emit the actual `pwa_installed` AFTER
      // `pwa_native_prompt_outcome` to preserve the strict funnel order
      // (outcome accepted → installed). Dedupe via the pre-mark snapshot so
      // a later real `appinstalled` still results in only one install event.
      const wasInstalled = getDebugSnapshot().installed;
      try { markInstalled(); } catch { /* ignore */ }
      try { registerPwaSuperProperties({ pwa_installed: true }); } catch { /* ignore */ }
      if (!wasInstalled) {
        const source = readInstallSource() || 'unknown';
        inferredInstall = { source };
      }
    } else {
      // User dismissed: clear the source we optimistically wrote on CTA
      // click. Otherwise it lingers in sessionStorage and the next install
      // (e.g. via browser menu) is wrongly attributed to `banner_cta`.
      clearInstallSource();
    }
    safeCapture('pwa_native_prompt_outcome', { outcome });
    if (inferredInstall) {
      safeCapture('pwa_installed', { source: inferredInstall.source, inferred: true });
    }
    deferredPrompt = null;
    return { outcome };
  } catch (err) {
    safeCapture('pwa_native_prompt_outcome', {
      outcome: 'error',
      message: String(err && err.message || err),
    });
    clearInstallSource();
    deferredPrompt = null;
    return { outcome: 'error' };
  }
}

export function _resetForTests() {
  deferredPrompt = null;
  initialized = false;
}

export const _internal = { INSTALL_SOURCE_KEY };
